// SEQ-240: "How is this worked out" text for the Dashboard's claims-by-month
// figures: the Monthly Claims Summary cards and the Claims Trend chart. Edit
// this file whenever those calculations change.
//
// WHAT IT DESCRIBES (line numbers at commit 3eed3d4, except where named):
// - useDashboardStats.ts 77-80 and 172-183: every project is fetched (no
//   status filter) and every claims_progress row is kept with its month, year
//   and schedule line.
// - useDashboardFilteredData.ts 232-346: the rows are narrowed to the
//   filtered projects (a row whose schedule line maps to no project is never
//   skipped, line 266), then to the date filter when one is set, then summed
//   into This Month, Last Month and Year to Date (calendar year) and into one
//   amount per month.
// - claimsTrendWindow.ts, called for filteredMonthlyBreakdown: with no date
//   filter, the 12 calendar months ending with this month, oldest first, a
//   month with no claim drawn at zero. With a date filter, the months in the
//   range that have a claim. Labels are "Mon YYYY".
// - Dashboard.tsx, the Monthly Claims Summary block (period cards, shown only
//   with a date filter, use filteredMonthlyBreakdown.slice(0, 2)) and the
//   Claims Trend card, headed "Claims Trend (Last 12 Months)" on All time and
//   "Claims Trend: <range>" with a date filter; DashboardCharts.tsx
//   ClaimsTrendChart (no slice).
//
// dashboardFigureDefinitions.test.ts checks every id here is used by a
// component and every id a component uses is here.

import type { MetricDefinition } from "../components/howWorkedOut/metricDefinition";

const FROM_CLAIM_LINES =
  "From the progress claim amounts entered against each project's schedule lines.";
const UNLINKED_LINE = "A claim line not linked to a project's schedule always counts, whatever the filters.";

export const CLAIMS_FIGURES = {
  "claims-summary.this-month": {
    name: "Claims this month",
    meaning: "The value claimed for this calendar month.",
    formula: ["Adds up every progress claim amount whose claim month is this month.", UNLINKED_LINE],
    source: { kind: "calculated", detail: FROM_CLAIM_LINES },
    projects: { scope: "filters" },
    dates: { scope: "calendar", detail: "This card shows only while the date filter is All time." },
    period: "This calendar month.",
  },
  "claims-summary.last-month": {
    name: "Claims last month",
    meaning: "The value claimed for last calendar month.",
    formula: ["Adds up every progress claim amount whose claim month is last month.", UNLINKED_LINE],
    source: { kind: "calculated", detail: FROM_CLAIM_LINES },
    projects: { scope: "filters" },
    dates: { scope: "calendar", detail: "This card shows only while the date filter is All time." },
    period: "Last calendar month.",
  },
  "claims-summary.year-to-date": {
    name: "Claims year to date",
    meaning: "The value claimed so far this calendar year.",
    formula: [
      "Adds up every progress claim amount whose claim month is from January to this month of this year.",
      UNLINKED_LINE,
    ],
    source: { kind: "calculated", detail: FROM_CLAIM_LINES },
    projects: { scope: "filters" },
    dates: { scope: "calendar", detail: "This card shows only while the date filter is All time." },
    period: "January to this month of this calendar year (not the financial year).",
  },
  "claims-summary.period-total": {
    name: "Claims total for the period",
    meaning: "The value claimed in the months of the date filter.",
    formula: ["Adds up every progress claim amount whose claim month falls in the date filter.", UNLINKED_LINE],
    source: { kind: "calculated", detail: FROM_CLAIM_LINES },
    projects: { scope: "filters" },
    dates: { scope: "date-filter" },
    period: "The months in the date filter.",
  },
  "claims-summary.period-month": {
    name: "Claims for one month",
    meaning: "The value claimed in this month of the date filter.",
    formula: [
      "Adds up every progress claim amount whose claim month is this month.",
      "Only the first two months in the range that have claims get a card.",
      UNLINKED_LINE,
    ],
    source: { kind: "calculated", detail: FROM_CLAIM_LINES },
    projects: { scope: "filters" },
    dates: { scope: "date-filter" },
    period: "The month named on the card.",
  },
  "claims-trend": {
    name: "Claims trend",
    meaning: "The value claimed in each month.",
    formula: [
      "Adds up every progress claim amount by its claim month.",
      "With the date filter on All time, it shows the last 12 months, ending with this month. A month with no claim is drawn at zero.",
      "With a date filter, it shows the months in the range that have a claim, and the heading names the range.",
      UNLINKED_LINE,
    ],
    source: { kind: "calculated", detail: FROM_CLAIM_LINES },
    projects: { scope: "filters" },
    dates: { scope: "date-filter", detail: "On All time, the last 12 months instead." },
    period: "The last 12 months, or the months in the date filter.",
  },
} satisfies Record<string, MetricDefinition>;
